import { createRef, useEffect, useMemo, useRef } from 'react';
import AttributeValueSelector from './attributeValueSelector';
import { useOktaAuth } from '@okta/okta-react';
import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { toast } from 'react-toastify';
import { useSearch } from '../../context/SearchContext';
import { useAttributes } from '../../hooks/useAttributes';

interface IAttributeSelectorProps {}

export const AttributeSelectors = (props: IAttributeSelectorProps) => {
  const attributeInputRefs = useRef<React.RefObject<HTMLDivElement>[]>([createRef(), createRef(), createRef()]);
  const { authState } = useOktaAuth();
  const { state, dispatch, initialFilters } = useSearch();
  const { filters, attributes, selectedIndexes, allIndexes } = state;

  // Empty selectedIndexes means "search all indexes"
  const indexesToFetch = selectedIndexes.length > 0 ? selectedIndexes : allIndexes;

  const {
    data: fetchedAttributes,
    isLoading: loadingAttributes,
    error: attributesError,
  } = useAttributes(authState?.accessToken?.accessToken || '', indexesToFetch, indexesToFetch.length > 0);

  const disableAdd = useMemo(() => {
    // Don't add if we have incomplete filters
    const hasIncompleteFilter = filters.some(
      (filter) => !filter.selectedValue && !filter.lowerBound && !filter.upperBound
    );
    if (hasIncompleteFilter) return true;

    // Don't add if we've reached the maximum number of available attributes
    const maxFilters = Math.max(attributes.length, 1); // At least allow 1 filter
    return filters.length >= maxFilters;
  }, [filters, attributes]);

  // With inverted selection model, empty selection means "All" - never disabled
  const disabled = false;
  // Sync fetched attributes with the context when they change
  useEffect(() => {
    if (fetchedAttributes) {
      dispatch({
        type: 'SYNC_FILTERS_WITH_ATTRIBUTES',
        payload: { attributes: fetchedAttributes, indexNames: selectedIndexes },
      });
    } else if (attributesError) {
      toast.error('Error fetching attributes.');
      dispatch({ type: 'SYNC_FILTERS_WITH_ATTRIBUTES', payload: { attributes: [], indexNames: [] } });
    }
  }, [fetchedAttributes, attributesError, dispatch, selectedIndexes]);

  useEffect(() => {
    if (!disableAdd) {
      dispatch({ type: 'ADD_FILTER' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disableAdd]);

  const handleRemove = (id: number) => {
    dispatch({ type: 'REMOVE_FILTER', payload: { id } });
  };

  const handleValueKeyDown = (idx: number) => (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      const lastInput = attributeInputRefs.current[idx + 1];
      if (lastInput) {
        if (lastInput.current) {
          lastInput.current.focus();
        }
      }
    }
  };

  const handleResetSearch = () => {
    dispatch({ type: 'RESET_FILTERS', payload: { initialFilters } });
  };

  return (
    <Box padding={2} sx={{ width: '100%', alignItems: 'stretch', position: 'relative', minHeight: 200 }}>
      {loadingAttributes && (
        <Box
          sx={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <CircularProgress />
        </Box>
      )}
      <Stack direction="column">
        <Stack direction="row" sx={{ textAlign: 'left', alignSelf: 'flex-start', opacity: disabled ? 0.5 : 1 }}>
          <Typography variant="h6">Apply Filters</Typography>
          <Button onClick={handleResetSearch} disabled={false}>
            Reset Search
          </Button>
        </Stack>
        <Box
          sx={{ width: '100%', alignItems: 'stretch', flexDirection: 'column' }}
          flexWrap="wrap"
          display="flex"
          justifyContent="flex-start"
          alignItems="center"
        >
          {filters.map((filter, idx) => {
            return (
              <AttributeValueSelector
                attributeRef={attributeInputRefs.current[idx]}
                key={filter.id}
                filter={filter}
                disabled={loadingAttributes}
                autoFocusValue={idx === 0}
                handleRemove={() => handleRemove(filter.id)}
                handleValueKeyDown={handleValueKeyDown(idx)}
              />
            );
          })}
        </Box>
      </Stack>
    </Box>
  );
};
